import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PRODUCT_REFERENCE, ProductReference } from "@/data/productReference";
import { Check, X, Minus, Scale, Download, ArrowRight, Star, Zap, Shield, Droplets, ThermometerSun } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface ComparisonFeature {
  label: string;
  key: keyof ProductReference | string;
  type: "text" | "boolean" | "list" | "price";
  category: "basic" | "features" | "specifications" | "hygiene" | "comfort";
  icon?: React.ReactNode;
}

const comparisonFeatures: ComparisonFeature[] = [
  // Basic Info
  { label: "Model Name", key: "avtName", type: "text", category: "basic" },
  { label: "Factory Model", key: "factoryModel", type: "text", category: "basic" },
  { label: "Series", key: "series", type: "text", category: "basic" },
  { label: "Category", key: "category", type: "text", category: "basic" },
  { label: "Price Range", key: "priceRange", type: "price", category: "basic", icon: <Star className="h-4 w-4" /> },
  
  // Features
  { label: "Instant Water Heating", key: "instantHeating", type: "boolean", category: "features", icon: <ThermometerSun className="h-4 w-4" /> },
  { label: "Heated Seat", key: "heatedSeat", type: "boolean", category: "features", icon: <ThermometerSun className="h-4 w-4" /> },
  { label: "Air Dryer", key: "airDryer", type: "boolean", category: "features", icon: <Zap className="h-4 w-4" /> },
  { label: "Remote Control", key: "remoteControl", type: "boolean", category: "features" },
  { label: "Motion Sensor", key: "motionSensor", type: "boolean", category: "features" },
  { label: "Night Light", key: "nightLight", type: "boolean", category: "features" },
  { label: "Auto Open/Close", key: "autoOpenClose", type: "boolean", category: "features" },
  
  // Hygiene
  { label: "UV Sterilization", key: "uvSterilization", type: "boolean", category: "hygiene", icon: <Shield className="h-4 w-4" /> },
  { label: "Self-Cleaning Nozzle", key: "selfCleaningNozzle", type: "boolean", category: "hygiene" },
  { label: "Deodorizer", key: "deodorizer", type: "boolean", category: "hygiene" },
  { label: "Foam Shield", key: "foamShield", type: "boolean", category: "hygiene" },
  
  // Comfort
  { label: "Bidet Function", key: "bidetFunction", type: "boolean", category: "comfort", icon: <Droplets className="h-4 w-4" /> },
  { label: "Oscillating Spray", key: "oscillatingSpray", type: "boolean", category: "comfort" },
  { label: "Adjustable Pressure", key: "adjustablePressure", type: "boolean", category: "comfort" },
  { label: "User Presets", key: "userPresets", type: "boolean", category: "comfort" },
  
  // Key Features List
  { label: "Key Features", key: "keyFeatures", type: "list", category: "specifications" },
];

// Derive feature values from product data
const getFeatureValue = (product: ProductReference, featureKey: string): any => {
  const keyFeatures = product.keyFeatures.map(f => f.toLowerCase());
  const description = product.description.toLowerCase();
  
  switch (featureKey) {
    case "instantHeating":
      return keyFeatures.some(f => f.includes("instant") && f.includes("heat"));
    case "heatedSeat":
      return keyFeatures.some(f => f.includes("heated seat") || f.includes("seat heating"));
    case "airDryer":
      return keyFeatures.some(f => f.includes("air dry") || f.includes("warm air"));
    case "remoteControl":
      return keyFeatures.some(f => f.includes("remote"));
    case "motionSensor":
      return keyFeatures.some(f => f.includes("motion") || f.includes("sensor") || f.includes("auto-open"));
    case "nightLight":
      return keyFeatures.some(f => f.includes("night light") || f.includes("ambient light") || f.includes("led light"));
    case "autoOpenClose":
      return keyFeatures.some(f => f.includes("auto-open") || f.includes("automatic lid"));
    case "uvSterilization":
      return keyFeatures.some(f => f.includes("uv") || f.includes("steriliz"));
    case "selfCleaningNozzle":
      return keyFeatures.some(f => f.includes("self-clean") || f.includes("nozzle clean"));
    case "deodorizer":
      return keyFeatures.some(f => f.includes("deodor"));
    case "foamShield":
      return keyFeatures.some(f => f.includes("foam"));
    case "bidetFunction":
      return keyFeatures.some(f => f.includes("bidet") || f.includes("wash")) || description.includes("bidet");
    case "oscillatingSpray":
      return keyFeatures.some(f => f.includes("oscillat") || f.includes("massage"));
    case "adjustablePressure":
      return keyFeatures.some(f => f.includes("pressure") || f.includes("adjustable"));
    case "userPresets":
      return keyFeatures.some(f => f.includes("memory") || f.includes("preset") || f.includes("user profile"));
    default:
      return (product as any)[featureKey];
  }
};

export const ProductComparisonTool: React.FC = () => {
  const products = Object.values(PRODUCT_REFERENCE);
  const [selectedProducts, setSelectedProducts] = useState<ProductReference[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const handleProductToggle = (product: ProductReference) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.internalCode === product.internalCode);
      if (isSelected) {
        return prev.filter(p => p.internalCode !== product.internalCode);
      } else if (prev.length < 4) {
        return [...prev, product];
      } else {
        toast.error("Maximum 4 products can be compared at once");
        return prev;
      }
    });
  };

  const clearSelection = () => {
    setSelectedProducts([]);
  };

  const exportComparison = () => {
    if (selectedProducts.length < 2) {
      toast.error("Select at least 2 products to export");
      return;
    }

    const headers = ["Feature", ...selectedProducts.map(p => p.avtName)];
    const rows = comparisonFeatures
      .filter(f => f.type !== "list")
      .map(feature => {
        const values = selectedProducts.map(p => {
          const value = getFeatureValue(p, feature.key);
          if (feature.type === "boolean") return value ? "Yes" : "No";
          return value || "-";
        });
        return [feature.label, ...values];
      });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `smart-toilet-comparison-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Comparison exported successfully");
  };

  const filteredFeatures = useMemo(() => {
    if (activeCategory === "all") return comparisonFeatures;
    return comparisonFeatures.filter(f => f.category === activeCategory);
  }, [activeCategory]);

  const renderFeatureValue = (product: ProductReference, feature: ComparisonFeature) => {
    const value = getFeatureValue(product, feature.key);

    if (feature.type === "boolean") {
      return value ? (
        <Check className="h-5 w-5 text-green-600 mx-auto" />
      ) : (
        <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />
      );
    }

    if (feature.type === "list" && Array.isArray(value)) {
      return (
        <ul className="text-xs space-y-1 text-left">
          {value.slice(0, 4).map((item: string, i: number) => (
            <li key={i} className="flex items-start gap-1">
              <Check className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
          {value.length > 4 && (
            <li className="text-primary font-medium">+{value.length - 4} more</li>
          )}
        </ul>
      );
    }

    if (feature.type === "price") {
      return (
        <Badge variant="outline" className="bg-accent/10 border-accent text-accent-foreground">
          {value}
        </Badge>
      );
    }

    return <span className="text-sm">{value || <Minus className="h-4 w-4 mx-auto text-muted-foreground" />}</span>;
  };

  return (
    <div className="space-y-8">
      {/* Selection Panel */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-accent" />
                Compare Smart Toilets
              </CardTitle>
              <CardDescription>
                Select up to 4 products to compare features, specifications, and benefits
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    Select Products ({selectedProducts.length}/4)
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Select Products to Compare</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    {products.map((product) => (
                      <div
                        key={product.internalCode}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedProducts.some(p => p.internalCode === product.internalCode)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => handleProductToggle(product)}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedProducts.some(p => p.internalCode === product.internalCode)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm">{product.avtName}</h4>
                            <p className="text-xs text-muted-foreground truncate">{product.tagline}</p>
                            <Badge variant="secondary" className="mt-2 text-xs">
                              {product.priceRange}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={clearSelection}>
                      Clear All
                    </Button>
                    <Button onClick={() => setIsDialogOpen(false)}>
                      Done ({selectedProducts.length} selected)
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              {selectedProducts.length >= 2 && (
                <Button variant="outline" onClick={exportComparison}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        {/* Selected Products Preview */}
        {selectedProducts.length > 0 && (
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedProducts.map((product) => (
                <Badge
                  key={product.internalCode}
                  variant="secondary"
                  className="px-3 py-1.5 cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  onClick={() => handleProductToggle(product)}
                >
                  {product.avtName}
                  <X className="h-3 w-3 ml-2" />
                </Badge>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Comparison Table */}
      {selectedProducts.length >= 2 && (
        <Card>
          <CardHeader>
            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className="w-full flex-wrap h-auto gap-1">
                <TabsTrigger value="all">All Features</TabsTrigger>
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="features">Smart Features</TabsTrigger>
                <TabsTrigger value="hygiene">Hygiene</TabsTrigger>
                <TabsTrigger value="comfort">Comfort</TabsTrigger>
                <TabsTrigger value="specifications">Specifications</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px] sticky left-0 bg-background">Feature</TableHead>
                  {selectedProducts.map((product) => (
                    <TableHead key={product.internalCode} className="text-center min-w-[180px]">
                      <Link 
                        to={product.urlSlug} 
                        className="hover:text-primary transition-colors font-semibold"
                      >
                        {product.avtName}
                      </Link>
                      <div className="text-xs font-normal text-muted-foreground mt-1">
                        {product.category}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Product Images Row */}
                <TableRow className="bg-muted/30">
                  <TableCell className="font-medium sticky left-0 bg-muted/30">Product Image</TableCell>
                  {selectedProducts.map((product) => (
                    <TableCell key={product.internalCode} className="text-center p-2">
                      <Link to={product.urlSlug}>
                        <img loading="lazy" decoding="async"
                          src={product.images.main}
                          alt={product.avtName}
                          className="w-24 h-24 object-cover rounded-lg mx-auto hover:scale-105 transition-transform"
                        />
                      </Link>
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Feature Rows */}
                {filteredFeatures.map((feature, index) => (
                  <TableRow key={feature.key} className={index % 2 === 0 ? "bg-muted/10" : ""}>
                    <TableCell className="font-medium sticky left-0 bg-background flex items-center gap-2">
                      {feature.icon}
                      {feature.label}
                    </TableCell>
                    {selectedProducts.map((product) => (
                      <TableCell key={product.internalCode} className="text-center">
                        {renderFeatureValue(product, feature)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* CTA Row */}
                <TableRow className="bg-primary/5">
                  <TableCell className="font-medium sticky left-0 bg-primary/5">View Details</TableCell>
                  {selectedProducts.map((product) => (
                    <TableCell key={product.internalCode} className="text-center p-4">
                      <Button asChild size="sm">
                        <Link to={product.urlSlug}>
                          Learn More
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {selectedProducts.length < 2 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Scale className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select Products to Compare</h3>
            <p className="text-muted-foreground mb-6">
              Choose at least 2 products to see a detailed feature comparison
            </p>
            <Button onClick={() => setIsDialogOpen(true)}>
              Select Products
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductComparisonTool;
